import { useState, useEffect, useCallback, useMemo } from "react";
import {
  projectService,
  CreateProjectInput,
  UpdateProjectInput,
} from "../services/projects";
import { Project } from "../../../shared/types";

export function useProjects(options?: { loadInactive?: boolean; initialStatus?: "active" | "archived" | "all" }) {
  const loadInactive = options?.loadInactive ?? false;
  const initialStatus = options?.initialStatus ?? "all";
  const [allProjects, setAllProjects] = useState<Project[]>([]); // Store all projects
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]); // Store filtered projects
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  type Filters = {
    status: "active" | "archived" | "all";
    type: "all" | "personal" | "shared";
    search: string;
    projectIds: string[];
    page: number;
    limit: number;
  };

  const [filters, setFilters] = useState<Filters>({
    status: initialStatus,
    type: "all",
    search: "",
    projectIds: [],
    page: 1,
    limit: 10,
  });

  // Client-side search and filtering function
  const applyFilters = useCallback(
    (projects: Project[], currentFilters: Filters) => {
      let filtered = [...projects];

      // Filter by status
      if (currentFilters.status !== "all") {
        filtered = filtered.filter(
          (project) => project.status === currentFilters.status
        );
      }

      // Filter by type
      if (currentFilters.type !== "all") {
        filtered = filtered.filter(
          (project) => project.type === currentFilters.type
        );
      }

      // Filter by project IDs
      if (currentFilters.projectIds && currentFilters.projectIds.length > 0) {
        filtered = filtered.filter((project) =>
          currentFilters.projectIds.includes(project.id)
        );
      }

      // Filter by search term
      if (currentFilters.search && currentFilters.search.trim()) {
        const searchLower = currentFilters.search.toLowerCase().trim();
        if (searchLower.length >= 2) {
          filtered = filtered.filter((project) => {
            // Helper function to normalize text while preserving umlauts and common special characters
            const normalizeText = (text: string) => {
              return text
                .toLowerCase()
                .normalize("NFD") // Decompose characters (e.g., ü -> u + ̈)
                .replace(/[\u0300-\u036f]/g, "") // Remove combining diacritical marks
                .replace(/ß/g, "ss") // Replace ß with ss for better matching
                .replace(/[^\w\säöü]/g, "") // Keep word chars, spaces, and German umlauts (excluding ß since we replaced it)
                .replace(/\s+/g, " ") // Normalize spaces
                .trim();
            };

            // Normalize both project name and search term
            const normalizedName = normalizeText(project.name);
            const normalizedSearch = normalizeText(searchLower);

            // Search in normalized project name (handles umlauts)
            const nameMatch = normalizedName.includes(normalizedSearch);

            // Search in original project name (case-insensitive, preserves umlauts)
            const originalNameMatch = project.name
              .toLowerCase()
              .includes(searchLower);

            // Search in project description (case-insensitive)
            const descriptionMatch =
              project.description &&
              project.description.toLowerCase().includes(searchLower);

            // Search in project type (case-insensitive)
            const typeMatch =
              project.type && project.type.toLowerCase().includes(searchLower);

            // Search in project status (case-insensitive)
            const statusMatch =
              project.status &&
              project.status.toLowerCase().includes(searchLower);

            // Also check if the project name starts with the search term (like SQL LIKE 'term%')
            const nameStartsWith = project.name
              .toLowerCase()
              .startsWith(searchLower);

            // Check if the project name ends with the search term (like SQL LIKE '%term')
            const nameEndsWith = project.name
              .toLowerCase()
              .endsWith(searchLower);

            return (
              nameMatch ||
              originalNameMatch ||
              descriptionMatch ||
              typeMatch ||
              statusMatch ||
              nameStartsWith ||
              nameEndsWith
            );
          });
        }
      }

      return filtered;
    },
    []
  );

  // Load all projects (no pagination, no search - just get everything)
  const loadAllProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const status = loadInactive ? "all" : "active";
      const response = await projectService.getProjects(status, 1, 1000);
      setAllProjects(response.projects);
    } catch (err) {
      console.error("Error loading projects:", err);
      setError("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [loadInactive]);

  // Apply filters and update pagination
  const applyFiltersAndPagination = useCallback(() => {
    const filtered = applyFilters(allProjects, filters);
    setFilteredProjects(filtered);

    // Calculate pagination
    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / filters.limit);
    const startIndex = (filters.page - 1) * filters.limit;
    const endIndex = startIndex + filters.limit;
    const paginatedProjects = filtered.slice(startIndex, endIndex);

    setPagination({
      page: filters.page,
      totalPages,
      totalCount,
      hasNextPage: filters.page < totalPages,
      hasPrevPage: filters.page > 1,
    });

    return paginatedProjects;
  }, [allProjects, filters, applyFilters]);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page ?? 1, // Reset to page 1 when filters change
    }));
  }, []);

  // Load projects with filters (this is the main function that components will call)
  const loadProjects = useCallback(
    async (
      status?: "active" | "archived" | "all",
      page?: number,
      limit?: number,
      search?: string
    ) => {
      // Update filters
      updateFilters({
        status: status || "all",
        page: page || 1,
        limit: limit || 10,
        search: search || "",
      });
    },
    [updateFilters]
  );

  // Create a new project
  const createProject = useCallback(
    async (input: CreateProjectInput) => {
      try {
        setLoading(true);
        setError(null);
        const response = await projectService.createProject(input);
        await loadAllProjects(); // Refresh the list
        return response;
      } catch (err: unknown) {
        console.error("Error creating project:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create project";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [loadAllProjects]
  );

  // Update a project
  const updateProject = useCallback(
    async (id: string, updates: UpdateProjectInput) => {
      try {
        setLoading(true);
        setError(null);
        const response = await projectService.updateProject(id, updates);

        // Update the project in the list
        setAllProjects((prev) =>
          prev.map((project) =>
            project.id === id ? response.project : project
          )
        );

        return response;
      } catch (err: unknown) {
        console.error("Error updating project:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update project";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Delete a project
  const deleteProject = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await projectService.deleteProject(id);

      // Check if the project was archived or actually deleted
      if (response.project) {
        if (loadInactive) {
          // Projects page (loadInactive:true) keeps archived projects visible for management
          setAllProjects((prev) =>
            prev.map((project) =>
              project.id === id ? response.project! : project
            )
          );
        } else {
          // Other pages only show active projects — remove the now-archived project
          setAllProjects((prev) => prev.filter((project) => project.id !== id));
        }
      } else {
        // Hard deleted — remove from list in all cases
        setAllProjects((prev) => prev.filter((project) => project.id !== id));
      }
    } catch (err: unknown) {
      console.error("Error deleting project:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete project";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadInactive]);

  // Get active projects only
  const getActiveProjects = useCallback(() => {
    return allProjects.filter((project) => project.status === "active");
  }, [allProjects]);

  // Restore an archived project
  const restoreProject = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await projectService.restoreProject(id);
      setAllProjects((prev) =>
        prev.map((project) => (project.id === id ? response.project : project))
      );
      return response;
    } catch (err: unknown) {
      console.error("Error restoring project:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to restore project";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply filters whenever filters or allProjects change
  useEffect(() => {
    if (allProjects.length > 0) {
      applyFiltersAndPagination();
    }
  }, [allProjects, filters, applyFiltersAndPagination]);

  // Load data on mount
  useEffect(() => {
    loadAllProjects();
  }, [loadAllProjects]);

  // Get the current page of projects
  const projects = useMemo(() => {
    const startIndex = (filters.page - 1) * filters.limit;
    const endIndex = startIndex + filters.limit;
    return filteredProjects.slice(startIndex, endIndex);
  }, [filteredProjects, filters.page, filters.limit]);

  return {
    projects,
    allProjects, // Expose all projects for dropdowns
    loading,
    error,
    pagination,
    createProject,
    updateProject,
    deleteProject,
    loadProjects,
    loadAllProjects,
    getActiveProjects,
    restoreProject,
    updateFilters,
    filters,
  };
}
